import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Button, Table, Tag, message, Card, Row, Col, Switch, Space, 
  Input, Select, DatePicker, Tooltip 
} from 'antd';
import { 
  SearchOutlined, CloseCircleOutlined, ReloadOutlined, 
  InfoCircleOutlined, CheckCircleOutlined, CloseCircleFilled,
  ClockCircleOutlined 
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { 
  getFundMonitor, getTransferLogs, refreshBankBalance, getBankList 
} from '../../services/api';

const { Option } = Select;
const { RangePicker } = DatePicker;

const FundMonitor = () => {
  const navigate = useNavigate();
  const [monitorData, setMonitorData] = useState([]);
  const [monitorLoading, setMonitorLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [banks, setBanks] = useState([]);
  const [autoUpdate, setAutoUpdate] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  // Filter States for Transfer Logs
  const [searchLogText, setSearchLogText] = useState('');
  const [selectedFrom, setSelectedFrom] = useState('all');
  const [selectedTo, setSelectedTo] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedTrigger, setSelectedTrigger] = useState('all');
  const [dateRange, setDateRange] = useState(null);

  const fetchMonitorData = useCallback(async () => {
    setMonitorLoading(true);
    try {
      const monitorRes = await getFundMonitor();
      setMonitorData(monitorRes.data || []);
    } catch (err) {
      message.error('Failed to load fund monitor data.');
    } finally {
      setMonitorLoading(false);
    }
  }, []);

  const fetchBanks = useCallback(async () => {
    try {
      const response = await getBankList();
      const records = Array.isArray(response.data?.data) ? response.data.data :
                      Array.isArray(response.data?.banks) ? response.data.banks :
                      Array.isArray(response.data) ? response.data : [];
      setBanks(records);
    } catch (err) {
      console.error('Failed to load bank accounts for filtering', err);
    }
  }, []);

  const fetchLogs = useCallback(async (page = 1, pageSize = 10, filterOverrides = {}) => {
    setLogsLoading(true);
    try {
      const activeFilters = {
        search: filterOverrides.search !== undefined ? filterOverrides.search : searchLogText,
        from_account: filterOverrides.from_account !== undefined ? filterOverrides.from_account : selectedFrom,
        to_account: filterOverrides.to_account !== undefined ? filterOverrides.to_account : selectedTo,
        status: filterOverrides.status !== undefined ? filterOverrides.status : selectedStatus,
        trigger_type: filterOverrides.trigger_type !== undefined ? filterOverrides.trigger_type : selectedTrigger,
        dateRange: filterOverrides.dateRange !== undefined ? filterOverrides.dateRange : dateRange,
      };

      const params = {
        page,
        limit: pageSize,
      };

      if (activeFilters.search && activeFilters.search.trim()) {
        params.search = activeFilters.search.trim();
      }
      if (activeFilters.from_account && activeFilters.from_account !== 'all') {
        params.from_account = activeFilters.from_account;
      }
      if (activeFilters.to_account && activeFilters.to_account !== 'all') {
        params.to_account = activeFilters.to_account;
      }
      if (activeFilters.status && activeFilters.status !== 'all') {
        params.status = activeFilters.status;
      }
      if (activeFilters.trigger_type && activeFilters.trigger_type !== 'all') {
        params.trigger_type = activeFilters.trigger_type;
      }
      if (activeFilters.dateRange && activeFilters.dateRange[0] && activeFilters.dateRange[1]) {
        params.start_date = activeFilters.dateRange[0].toISOString();
        params.end_date = activeFilters.dateRange[1].toISOString();
      }

      const logsRes = await getTransferLogs(params);
      const logsData = logsRes.data;
      const logRecords = Array.isArray(logsData.data) ? logsData.data : 
                         Array.isArray(logsData.logs) ? logsData.logs :
                         Array.isArray(logsData) ? logsData : [];
      setLogs(logRecords);

      if (logsData.pagination) {
        setPagination({
          current: logsData.pagination.page,
          pageSize: logsData.pagination.limit,
          total: logsData.pagination.total,
        });
      } else if (logsData.total !== undefined) {
        setPagination(prev => ({ 
          ...prev, 
          total: logsData.total, 
          current: logsData.page || page,
          pageSize: logsData.limit || pageSize 
        }));
      }
    } catch (err) {
      message.error('Failed to load transfer logs.');
    } finally {
      setLogsLoading(false);
    }
  }, [searchLogText, selectedFrom, selectedTo, selectedStatus, selectedTrigger, dateRange]);

  // Initial load
  useEffect(() => {
    fetchMonitorData();
    fetchLogs(1, 10);
    fetchBanks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search for logs
  const isSearchMounted = useRef(false);
  useEffect(() => {
    if (!isSearchMounted.current) {
      isSearchMounted.current = true;
      return;
    }
    const timer = setTimeout(() => {
      fetchLogs(1, pagination.pageSize, { search: searchLogText });
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchLogText]);

  // Auto-update interval setup for monitor data
  useEffect(() => {
    let interval;
    if (autoUpdate) {
      interval = setInterval(() => {
        fetchMonitorData();
      }, 10000); // 10 seconds
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [autoUpdate, fetchMonitorData]);

  const handleFromChange = (val) => {
    const value = val !== undefined && val !== null ? val : 'all';
    setSelectedFrom(value);
    fetchLogs(1, pagination.pageSize, { from_account: value });
  };

  const handleToChange = (val) => {
    const value = val !== undefined && val !== null ? val : 'all';
    setSelectedTo(value);
    fetchLogs(1, pagination.pageSize, { to_account: value });
  };

  const handleStatusChange = (val) => {
    const value = val || 'all';
    setSelectedStatus(value);
    fetchLogs(1, pagination.pageSize, { status: value });
  };

  const handleTriggerChange = (val) => {
    const value = val || 'all';
    setSelectedTrigger(value);
    fetchLogs(1, pagination.pageSize, { trigger_type: value });
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
    fetchLogs(1, pagination.pageSize, { dateRange: dates });
  };

  const handleResetFilters = () => {
    setSearchLogText('');
    setSelectedFrom('all');
    setSelectedTo('all');
    setSelectedStatus('all');
    setSelectedTrigger('all');
    setDateRange(null);
    fetchLogs(1, pagination.pageSize, {
      search: '',
      from_account: 'all',
      to_account: 'all',
      status: 'all',
      trigger_type: 'all',
      dateRange: null,
    });
  };

  const isFiltered = Boolean(
    (searchLogText && searchLogText.trim() !== '') ||
    selectedFrom !== 'all' ||
    selectedTo !== 'all' ||
    selectedStatus !== 'all' ||
    selectedTrigger !== 'all' ||
    Boolean(dateRange && dateRange[0] && dateRange[1])
  );

  const handleTableChange = (newPagination) => {
    fetchLogs(newPagination.current, newPagination.pageSize);
  };

  const handleRefreshBalance = async (bankId) => {
    try {
      await refreshBankBalance(bankId);
      message.success('Balance refresh requested.');
      fetchMonitorData();
    } catch (err) {
      message.error('Failed to refresh balance.');
    }
  };

  const formatCurrency = (amount) => 
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);

  const logColumns = [
    { 
      title: 'Date & Time', 
      dataIndex: 'created_at', 
      key: 'created_at', 
      width: 175,
      render: (ts) => ts ? new Date(ts).toLocaleString() : 'N/A' 
    },
    { 
      title: 'From Account', 
      dataIndex: 'from_account_name', 
      key: 'from_account_name',
      render: (text) => <strong>{text || 'N/A'}</strong>
    },
    { 
      title: 'To Account', 
      dataIndex: 'to_account_name', 
      key: 'to_account_name',
      render: (text) => <strong>{text || 'N/A'}</strong>
    },
    { 
      title: 'Amount', 
      dataIndex: 'amount', 
      key: 'amount', 
      width: 130,
      render: (val) => val !== undefined && val !== null ? (
        <span style={{ fontWeight: 600, color: '#1677ff' }}>
          ₹{Number(val).toLocaleString('en-IN')}
        </span>
      ) : '₹0'
    },
    { 
      title: 'Trigger', 
      dataIndex: 'trigger_type', 
      key: 'trigger_type', 
      width: 100,
      render: (val) => {
        const type = (val || 'AUTO').toUpperCase();
        return (
          <Tag color={type === 'AUTO' ? 'cyan' : 'purple'}>
            {type}
          </Tag>
        );
      }
    },
    { 
      title: 'Status', 
      dataIndex: 'status', 
      key: 'status', 
      width: 120,
      render: (val, record) => {
        const status = (val || 'PENDING').toUpperCase();
        if (status === 'SUCCESS') {
          return (
            <Tag icon={<CheckCircleOutlined />} color="success">
              SUCCESS
            </Tag>
          );
        } else if (status === 'FAILED') {
          return (
            <Tooltip title={record.error_msg || 'Transfer failed'}>
              <Tag icon={<CloseCircleFilled />} color="error" style={{ cursor: 'pointer' }}>
                FAILED
              </Tag>
            </Tooltip>
          );
        } else {
          return (
            <Tag icon={<ClockCircleOutlined />} color="warning">
              {status}
            </Tag>
          );
        }
      }
    },
    { 
      title: 'UTR', 
      dataIndex: 'utr', 
      key: 'utr',
      render: (val) => val ? (
        <Tag color="blue" style={{ fontFamily: 'monospace' }}>{val}</Tag>
      ) : (
        <span style={{ color: '#bfbfbf' }}>—</span>
      )
    },
    {
      title: 'Details / Error',
      dataIndex: 'error_msg',
      key: 'error_msg',
      render: (msg) => msg ? (
        <Tooltip title={msg}>
          <span style={{ color: '#ff4d4f', fontSize: 12, maxWidth: 180, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <InfoCircleOutlined style={{ marginRight: 4 }} />
            {msg}
          </span>
        </Tooltip>
      ) : (
        <span style={{ color: '#bfbfbf' }}>—</span>
      )
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0 }}>Fund Dashboard / Monitor</h2>
          <span style={{ fontSize: 13, color: '#8c8c8c' }}>
            Real-time balance monitoring and automated fund transfer audit logs
          </span>
        </div>
        <Space size="middle">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>Auto-update Monitor: </span>
            <Switch 
              checked={autoUpdate} 
              onChange={(checked) => setAutoUpdate(checked)} 
            />
          </div>
          <Button onClick={() => navigate('/fund-management/rules')}>Manage Rules</Button>
        </Space>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Live Monitor</h3>
        <Tooltip title="Refresh live balances">
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchMonitorData} 
            loading={monitorLoading}
            size="small"
          >
            Refresh All
          </Button>
        </Tooltip>
      </div>

      <Row gutter={[16, 16]}>
        {monitorData.map(acc => {
          return (
            <Col key={acc.rule_id} xs={24} sm={12} md={8}>
              <Card
                title={acc.source_account}
                extra={<Button size="small" onClick={() => handleRefreshBalance(acc.rule_id)}>Refresh</Button>}
              >
                <p><strong>Current Balance:</strong> {formatCurrency(acc.current_balance)}</p>
                <p><strong>Threshold:</strong> {formatCurrency(acc.threshold)}</p>
                <p><strong>Beneficiary:</strong> {acc.beneficiary_account_name}</p>
                <p><strong>Status:</strong> <Tag color={acc.is_active ? 'blue' : 'default'}>{acc.status_label}</Tag></p>
                <p><strong>Last Transfer:</strong> {acc.last_transfer_at ? new Date(acc.last_transfer_at).toLocaleString() : 'N/A'}</p>
              </Card>
            </Col>
          );
        })}
      </Row>

      <div style={{ marginTop: 36, marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Transfer Audit Logs</h3>
      </div>

      {/* Transfer Logs Filter Toolbar */}
      <Card
        size="small"
        style={{
          marginBottom: 16,
          backgroundColor: '#fafafa',
          borderRadius: 8,
          border: '1px solid #f0f0f0',
        }}
        bodyStyle={{ padding: '12px 16px' }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <Space wrap size="middle" style={{ flex: 1 }}>
            {/* Search Input */}
            <Input
              placeholder="Search UTR, account, error..."
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              allowClear
              value={searchLogText}
              onChange={(e) => setSearchLogText(e.target.value)}
              style={{ width: 240 }}
            />

            {/* From Account */}
            <Select
              placeholder="All Source Accounts"
              value={selectedFrom}
              onChange={handleFromChange}
              style={{ width: 200 }}
              showSearch
              optionFilterProp="children"
            >
              <Option value="all">All Source Accounts</Option>
              {banks.map((b) => (
                <Option key={`from-${b.id}`} value={b.id}>
                  {b.account_name || b.name || 'Account'} ({b.bank_login})
                </Option>
              ))}
            </Select>

            {/* To Account */}
            <Select
              placeholder="All Target Accounts"
              value={selectedTo}
              onChange={handleToChange}
              style={{ width: 200 }}
              showSearch
              optionFilterProp="children"
            >
              <Option value="all">All Target Accounts</Option>
              {banks.map((b) => (
                <Option key={`to-${b.id}`} value={b.id}>
                  {b.account_name || b.name || 'Account'} ({b.bank_login})
                </Option>
              ))}
            </Select>

            {/* Status */}
            <Select
              value={selectedStatus}
              onChange={handleStatusChange}
              style={{ width: 130 }}
            >
              <Option value="all">All Status</Option>
              <Option value="SUCCESS">SUCCESS</Option>
              <Option value="FAILED">FAILED</Option>
              <Option value="PENDING">PENDING</Option>
            </Select>

            {/* Trigger */}
            <Select
              value={selectedTrigger}
              onChange={handleTriggerChange}
              style={{ width: 130 }}
            >
              <Option value="all">All Triggers</Option>
              <Option value="AUTO">AUTO</Option>
              <Option value="MANUAL">MANUAL</Option>
            </Select>

            {/* Date Range Picker */}
            <RangePicker
              value={dateRange}
              onChange={handleDateChange}
              style={{ width: 240 }}
              placeholder={['Start Date', 'End Date']}
            />

            {/* Reset Button */}
            <Button
              icon={<CloseCircleOutlined />}
              onClick={handleResetFilters}
              disabled={!isFiltered}
            >
              Reset
            </Button>
          </Space>

          {/* Results count & Refresh */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#8c8c8c' }}>
              Showing {logs.length} of {pagination.total || 0} {pagination.total === 1 ? 'log' : 'logs'}
            </span>
            {isFiltered && (
              <Tag color="blue" closable onClose={handleResetFilters}>
                Filtered
              </Tag>
            )}
            <Tooltip title="Refresh transfer logs">
              <Button
                type="text"
                icon={<ReloadOutlined />}
                onClick={() => fetchLogs(pagination.current, pagination.pageSize)}
                loading={logsLoading}
              />
            </Tooltip>
          </div>
        </div>
      </Card>

      <Table
        dataSource={logs}
        columns={logColumns}
        loading={logsLoading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} logs`,
        }}
        onChange={handleTableChange}
        rowKey="id"
      />
    </div>
  );
};

export default FundMonitor;